import { Form } from "radix-ui";
import { Callout, Flex, Text } from "@radix-ui/themes";
import { useMe, useSendVerification, useVerify } from "../api/endpoint.js";
import { useElevatedAccessToken } from "../api/api-provider.js";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { AlertDialog, Dialog, Button } from "./elements.js";
import * as Otp from "./otp-input.js";
import { Translation } from "./i18n/translation.js";

interface ElevatedAccessProps extends PropsWithChildren {
  onVerified?: () => Promise<unknown>;
  type?: "dialog" | "alert";
}

export function ElevatedAccess({
  type = "dialog",
  children,
  onVerified,
}: ElevatedAccessProps) {
  const { elevatedAccess } = useElevatedAccessToken();
  const [authenticationChallengeId, setAuthenticationChallengeId] =
    useState<string>();

  const prevAccessToken = useRef(elevatedAccess);

  useEffect(() => {
    prevAccessToken.current = elevatedAccess;
  }, [elevatedAccess]);

  if (elevatedAccess) {
    return <>{children}</>;
  }

  if (!authenticationChallengeId) {
    // FIXME: This should be refactored
    // eslint-disable-next-line react-hooks/refs
    const hasTokenExpired = !!prevAccessToken.current;

    return (
      <SendVerificationEmailForm
        type={type}
        hasTokenExpired={hasTokenExpired}
        onSuccess={(challengeId) => {
          setAuthenticationChallengeId(challengeId);
        }}
      />
    );
  }

  if (authenticationChallengeId) {
    return (
      <VerificationIdentityForm
        type={type}
        authenticationChallengeId={authenticationChallengeId}
        onSuccess={() => {
          // Reset the challenge id
          setAuthenticationChallengeId(undefined);

          return onVerified?.();
        }}
      />
    );
  }

  return null;
}

interface SendVerificationEmailFormProps {
  onSuccess: (challengeId: string) => unknown | Promise<unknown>;
  type: "dialog" | "alert";
  hasTokenExpired: boolean;
}

function SendVerificationEmailForm({
  onSuccess,
  type,
  hasTokenExpired,
}: SendVerificationEmailFormProps) {
  const { data: me } = useMe();
  const sendVerification = useSendVerification({
    mutation: { onSuccess: (data) => onSuccess(data.authenticationChallenge) },
  });

  const Title = type === "dialog" ? Dialog.Title : AlertDialog.Title;
  const Description =
    type === "dialog" ? Dialog.Description : AlertDialog.Description;
  const Close = type === "dialog" ? Dialog.Close : AlertDialog.Cancel;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    sendVerification.mutate();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Title>
        {hasTokenExpired ? (
          <Translation
            defaultMessage="Your verification token has expired"
            id="lb/AsC"
            description="Dialog title when verification token has expired"
          />
        ) : (
          <Translation
            defaultMessage="Verify your identity"
            id="7tGCOh"
            description="Dialog title for identity verification"
          />
        )}
      </Title>

      <Description mb="5">
        <Translation
          defaultMessage="To continue, we need to confirm your identity. We'll send a temporary verification code to {email}."
          id="FTUSRM"
          description="Description explaining verification code will be sent"
          values={{
            email: (
              <Text weight="bold" highContrast>
                {me?.email}
              </Text>
            ),
          }}
        />
      </Description>

      {sendVerification.error && (
        <Callout.Root color="red" mt="-2" mb="0">
          <Callout.Text>
            {getMutationErrorMessage(sendVerification.error)}
          </Callout.Text>
        </Callout.Root>
      )}

      <Flex justify="end" align="center" gap="3" mt="5">
        <Close>
          <Button
            variant="secondary"
            type="button"
            disabled={sendVerification.isPending}
          >
            <Translation
              defaultMessage="Cancel"
              id="hHNj31"
              description="Cancel button text"
            />
          </Button>
        </Close>
        <Button type="submit" loading={sendVerification.isPending}>
          <Translation
            defaultMessage="Send verification code"
            id="XsTeXJ"
            description="Button text to send verification code"
          />
        </Button>
      </Flex>
    </form>
  );
}

interface VerificationIdentityFormProps {
  onSuccess?: () => unknown | Promise<unknown>;
  authenticationChallengeId: string;
  type: "dialog" | "alert";
}

function VerificationIdentityForm({
  onSuccess,
  authenticationChallengeId,
  type,
}: VerificationIdentityFormProps) {
  const { data: me } = useMe();
  const { setElevatedAccess } = useElevatedAccessToken();
  const verifyIdentity = useVerify();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const Title = type === "dialog" ? Dialog.Title : AlertDialog.Title;
  const Description =
    type === "dialog" ? Dialog.Description : AlertDialog.Description;
  const Close = type === "dialog" ? Dialog.Close : AlertDialog.Cancel;
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const code = formData.get("otp-code")?.toString() ?? "";

    setIsSubmitting(true);

    try {
      // Mutate async so we can wait for the onSuccess callback as well
      const newAuthState = await verifyIdentity.mutateAsync({
        data: {
          code,
          authenticationChallengeId,
        },
      });

      const in10Seconds = new Date(Date.now() + 5 * 1000);

      setElevatedAccess({
        token: newAuthState.elevatedAccessToken,
        // expiresAt: newAuthState.expiresAt,
        expiresAt: in10Seconds.toISOString(),
      });

      if (onSuccess) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        await onSuccess();
      }
    } catch (error) {
      console.error(error);
    }

    setIsSubmitting(false);
  };

  return (
    <Form.Root onSubmit={handleSubmit}>
      <Title>
        <Translation
          defaultMessage="Verify your identity"
          id="7tGCOh"
          description="Dialog title for identity verification"
        />
      </Title>

      <Description>
        <Translation
          defaultMessage="A verification code was sent to {email}. Please enter it below."
          id="kvxE0S"
          description="Description explaining where verification code was sent"
          values={{
            email: (
              <Text weight="bold" highContrast>
                {me?.email}
              </Text>
            ),
          }}
        />
      </Description>

      <Flex direction="column" gap="2" mt="5" mx="auto" width="fit-content">
        <Otp.Root
          autoSubmit
          gap="2"
          justify="center"
          columns="repeat(6, 48px)"
          width="fit-content"
          rows="48px"
          name="otp-code"
          readOnly={isSubmitting}
        >
          <Otp.Input required autoFocus autoComplete="off" />
          <Otp.Input required />
          <Otp.Input required />
          <Otp.Input required />
          <Otp.Input required />
          <Otp.Input required />
        </Otp.Root>

        {verifyIdentity.error && (
          <Text color="red" size="2" as="p">
            {getMutationErrorMessage(verifyIdentity.error)}
          </Text>
        )}
      </Flex>

      <Flex justify="end" align="center" gap="3" mt="5">
        <Close>
          <Button variant="secondary" type="button">
            <Translation
              defaultMessage="Cancel"
              id="hHNj31"
              description="Cancel button text"
            />
          </Button>
        </Close>
        <Button type="submit" loading={isSubmitting}>
          <Translation
            defaultMessage="Confirm"
            id="EmYENK"
            description="Confirm button text for verification"
          />
        </Button>
      </Flex>
    </Form.Root>
  );
}

function getMutationErrorMessage(error: unknown) {
  let message = typeof error === "string" ? error : "";

  if (error instanceof Error) {
    message = error.message;
  } else if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    message = error.message;
  }

  if (!message || message === "Bad Request") {
    message = "Invalid code, please try again.";
  }

  return message;
}

// Note: Error messages in getMutationErrorMessage are kept as plain strings
// since they are displayed dynamically
